import React, { useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Plus, Edit2, Trash2, X, Upload, Download } from "lucide-react";

const empty = { name: "", floor: "", shop_no: "", category: "", logo_url: "", status: "active" };

export default function Brands() {
  const [items, setItems] = useState([]);
  const [floors, setFloors] = useState([]);
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/brands").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/floors").then(r => setFloors(r.data));
    api.get("/categories").then(r => setCats(r.data));
  }, []);

  const openNew = () => { setForm(empty); setModal("new"); };
  const openEdit = (b) => { setForm({ ...b }); setModal(b.id); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === "new") await api.post("/brands", form);
      else await api.patch(`/brands/${modal}`, form);
      setModal(null); load();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this brand?")) return;
    await api.delete(`/brands/${id}`); load();
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/brands/bulk-import", fd);
      setImportResult(r.data);
      load();
    } catch (e) {
      setImportResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const csv = "name,floor,shop_no,category,logo_url,status\nDhaba,Ground,G-12,Restaurant,,active\nGiva,First,F-18,Jewellery,,active\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "brands_template.csv";
    a.click();
  };

  return (
    <Layout
      title="Brands" subtitle="Management"
      action={
        <div className="flex gap-2">
          <button className="btn-outline flex items-center gap-2" onClick={() => setImportOpen(true)} data-testid="bulk-import-btn"><Upload size={14} /> Bulk import</button>
          <button className="btn-dark flex items-center gap-2" onClick={openNew} data-testid="add-brand-btn"><Plus size={14} /> Add brand</button>
        </div>
      }
    >
      <div className="card-sharp" data-testid="brands-table">
        <table className="fin">
          <thead>
            <tr><th>Brand</th><th>Category</th><th>Floor</th><th>Shop No</th><th>Status</th><th style={{width:120}}></th></tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} data-testid={`brand-row-${b.id}`}>
                <td className="font-medium">{b.name}</td>
                <td className="text-[#595959]">{b.category || "—"}</td>
                <td className="text-[#595959]">{b.floor || "—"}</td>
                <td className="text-[#595959]">{b.shop_no || "—"}</td>
                <td><span className={`badge badge-${b.status}`}>{b.status}</span></td>
                <td>
                  <div className="flex items-center gap-2 justify-end">
                    <button className="btn-outline" style={{padding:"6px 10px"}} onClick={() => openEdit(b)} data-testid={`edit-brand-${b.id}`}><Edit2 size={12} /></button>
                    <button className="btn-outline" style={{padding:"6px 10px"}} onClick={() => remove(b.id)} data-testid={`del-brand-${b.id}`}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-[#8C8C8C]">No brands yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal onClose={() => setModal(null)} title={modal === "new" ? "Add brand" : "Edit brand"}>
          <form onSubmit={save} className="space-y-4">
            <div><div className="label-tiny mb-1.5">Name</div><input className="fin" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="brand-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-tiny mb-1.5">Floor</div>
                <select className="fin" value={form.floor||""} onChange={(e) => setForm({...form, floor: e.target.value})} data-testid="brand-floor">
                  <option value="">— select floor —</option>
                  {floors.filter(f => f.status === "active").map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                </select>
              </div>
              <div><div className="label-tiny mb-1.5">Shop No</div><input className="fin" value={form.shop_no||""} onChange={(e) => setForm({...form, shop_no: e.target.value})} data-testid="brand-shop" /></div>
            </div>
            <div>
              <div className="label-tiny mb-1.5">Category</div>
              <select className="fin" value={form.category||""} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="brand-category">
                <option value="">— select category —</option>
                {cats.filter(c => c.status === "active").map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div><div className="label-tiny mb-1.5">Logo URL (optional)</div><input className="fin" value={form.logo_url||""} onChange={(e) => setForm({...form, logo_url: e.target.value})} data-testid="brand-logo" /></div>
            <div><div className="label-tiny mb-1.5">Status</div>
              <select className="fin" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} data-testid="brand-status">
                <option value="active">Active</option><option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" disabled={saving} className="btn-dark" data-testid="save-brand-btn">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {importOpen && (
        <Modal onClose={() => { setImportOpen(false); setImportResult(null); }} title="Bulk import brands">
          <div className="space-y-4">
            <p className="text-sm text-[#595959]">Upload a CSV file with columns: <code className="bg-[#F9F8F6] px-1 text-[12px]">name, floor, shop_no, category, logo_url, status</code></p>
            <p className="text-[12px] text-[#8C8C8C]"><span className="font-medium">name</span> is required. Rows where a brand with that name already exists will be updated; new ones will be created.</p>

            <button className="btn-outline flex items-center gap-2" onClick={downloadTemplate} data-testid="dl-template-btn"><Download size={14} /> Download CSV template</button>

            <div className="border-2 border-dashed border-[#E6E4E0] p-8 text-center">
              <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => doImport(e.target.files?.[0])} data-testid="bulk-file-input" />
              <button className="btn-dark flex items-center gap-2 mx-auto" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="choose-csv-btn">
                <Upload size={14} /> {importing ? "Importing…" : "Choose CSV file"}
              </button>
            </div>

            {importResult && (
              <div className={`p-4 border ${importResult.error ? "bg-[#F0E4E0] border-[#8C3A3A]/30 text-[#8C3A3A]" : "bg-[#E8EFE0] border-[#4A6741]/30 text-[#4A6741]"}`} data-testid="import-result">
                {importResult.error ? (
                  <div>Error: {importResult.error}</div>
                ) : (
                  <div className="text-sm">
                    <div className="font-semibold mb-1">Import complete</div>
                    <div>Created: {importResult.created} · Updated: {importResult.updated} · Skipped: {importResult.skipped}</div>
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2 text-[12px]">Errors: {importResult.errors.join("; ")}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button className="btn-outline" onClick={() => { setImportOpen(false); setImportResult(null); }}>Close</button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
}

export function Modal({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={onClose}>
      <div className="bg-white border border-[#E6E4E0] w-full max-w-md p-7 fade-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div><div className="label-tiny">Form</div><div className="text-xl font-light" style={{fontFamily:"Outfit"}}>{title}</div></div>
          <button onClick={onClose} className="text-[#8C8C8C] hover:text-black" data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
